import { useState, useRef } from 'react';
import { API_ENDPOINTS } from '../constants/apiConfig';
import { safeEvaluatePattern } from '../utils/safePatternEvaluator';
import { classifyFetchError } from '../utils/errorHandler';

/**
 * usePromptGeneration Hook
 *
 * Handles AI-powered pattern generation from text prompts.
 * Manages the entire flow: API call, code evaluation, pattern setting.
 * Includes automatic retry logic for connection failures.
 */
export const usePromptGeneration = ({ onSuccess, onError, onPatternGenerated }) => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [prompt, setPrompt] = useState('');
  const [customPattern, setCustomPattern] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isDisabled, setIsDisabled] = useState(false);
  const [disabledUntil, setDisabledUntil] = useState(null);
  const retryTimeoutRef = useRef(null);
  const disableTimeoutRef = useRef(null);

  const generateFromPrompt = async (isRetry = false, currentRetryCount = 0) => {
    if (!prompt.trim()) return;

    // Check if generation is disabled
    if (isDisabled && disabledUntil) {
      const now = Date.now();
      if (now < disabledUntil) {
        const remainingMinutes = Math.ceil((disabledUntil - now) / 60000);
        if (onError) {
          onError({
            type: 'DISABLED',
            title: 'Generation temporarily disabled',
            message: `Please wait ${remainingMinutes} more ${remainingMinutes === 1 ? 'minute' : 'minutes'} before trying again.`,
            suggestions: [
              'This prevents overwhelming the API',
              'The button will re-enable automatically'
            ],
            retryable: false,
            duration: 4000
          });
        }
        return;
      } else {
        // Disabled period has expired, re-enable
        setIsDisabled(false);
        setDisabledUntil(null);
      }
    }

    // Reset retry count on new user-initiated request
    if (!isRetry) {
      setRetryCount(0);
      currentRetryCount = 0;
      // Clear any pending retry
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }

    setIsGenerating(true);

    // Create abort controller for timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout

    try {
      const response = await fetch(API_ENDPOINTS.generatePattern, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          prompt: prompt
        }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      let data;
      try {
        data = await response.json();
      } catch (parseError) {
        // If we can't parse JSON, create a basic error
        throw new Error('Failed to parse server response');
      }

      if (!response.ok) {
        const error = new Error(data.error || 'Failed to generate pattern');
        error.errorInfo = classifyFetchError(error, response, data);
        error.response = response;
        error.data = data;
        throw error;
      }

      const codeText = data.content.find(item => item.type === "text")?.text || "";

      try {
        const patternFunc = safeEvaluatePattern(codeText);
        setCustomPattern(() => patternFunc);

        // Pass raw code to parent for storage in chat history
        if (onPatternGenerated) {
          onPatternGenerated(codeText, prompt);
        }

        // Reset retry count and clear disabled state on success
        setRetryCount(0);
        setIsDisabled(false);
        setDisabledUntil(null);
        if (disableTimeoutRef.current) {
          clearTimeout(disableTimeoutRef.current);
          disableTimeoutRef.current = null;
        }

        if (onSuccess) {
          onSuccess();
        }

      } catch (err) {
        console.error("Pattern function error:", err);
        if (onError) {
          onError({
            type: 'PATTERN_ERROR',
            title: 'Invalid pattern',
            message: 'Generated pattern had an error. Try rephrasing your prompt or try again.',
            suggestions: [
              'Try a different prompt',
              'Be more specific in your description',
              'Try again - the AI generates different results each time'
            ],
            retryable: true,
            duration: 5000
          });
        }
      }

    } catch (errorObj) {
      clearTimeout(timeoutId);

      // Handle both direct errors and our wrapped errors
      const error = errorObj.error || errorObj;
      const response = errorObj.response || null;
      const data = errorObj.data || null;
      const errorInfo = errorObj.errorInfo || classifyFetchError(error, response, data);


      // Retry logic for connection failures only
      const MAX_RETRIES = 2;
      if (errorInfo.type === 'CONNECTION_FAILED' && currentRetryCount < MAX_RETRIES) {
        const nextRetryCount = currentRetryCount + 1;
        const retryDelay = Math.pow(2, currentRetryCount) * 1000; // 1s, 2s

        // Don't show error toast during retries - just silently retry
        // Only show final error if all retries fail
        setRetryCount(nextRetryCount);
        retryTimeoutRef.current = setTimeout(() => {
          generateFromPrompt(true, nextRetryCount); // Retry with updated count
        }, retryDelay);

        return; // Don't set isGenerating to false yet
      }

      // Disable generation if the error requires it
      if (errorInfo.disableFor && errorInfo.disableFor > 0) {
        const disableUntilTime = Date.now() + errorInfo.disableFor;
        setIsDisabled(true);
        setDisabledUntil(disableUntilTime);

        // Auto re-enable after the disable period
        disableTimeoutRef.current = setTimeout(() => {
          setIsDisabled(false);
          setDisabledUntil(null);
        }, errorInfo.disableFor);

      }

      // Show error to user only after all retries exhausted
      if (onError) {
        // Add retry attempt info to the message if retries were attempted
        if (currentRetryCount > 0) {
          errorInfo.message = `${errorInfo.message} (Failed after ${currentRetryCount} ${currentRetryCount === 1 ? 'retry' : 'retries'})`;
        }
        onError(errorInfo);
      }

      // Reset retry count after final attempt
      setRetryCount(0);
    }

    setIsGenerating(false);
  };

  const clearPattern = () => {
    setCustomPattern(null);
    setPrompt('');
    setRetryCount(0);
    // Clear any pending retry
    if (retryTimeoutRef.current) {
      clearTimeout(retryTimeoutRef.current);
      retryTimeoutRef.current = null;
    }
    // Don't clear disabled state - keep it active
  };

  return {
    isGenerating,
    prompt,
    setPrompt,
    customPattern,
    setCustomPattern,
    generateFromPrompt,
    clearPattern,
    retryCount,
    isDisabled,
    disabledUntil
  };
};
